import { useEffect, useMemo, useState } from "react";
import type { OcPortalConfig, OcFlowState } from "../App";
import { ocReset } from "../lib/oc-api";
import { OcPasswordStrengthMeter } from "../components/OcPasswordStrengthMeter";

/**
 * Generate a cryptographically random password that satisfies a common
 * policy (16 chars: ≥1 uppercase, ≥1 lowercase, ≥1 digit, ≥1 symbol).
 * Ambiguous characters (O/0, I/l/1, |, `) are excluded so the user can
 * read and type the generated value reliably if they copy it elsewhere.
 */
function ocGeneratePassword(): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const digit = "23456789";
  const symbol = "!@#$%^&*-_=+";
  const all = upper + lower + digit + symbol;
  const len = 16;
  const rand = (chars: string) =>
    chars[crypto.getRandomValues(new Uint32Array(1))[0]! % chars.length]!;

  // Seed one of each required class first.
  const out: string[] = [rand(upper), rand(lower), rand(digit), rand(symbol)];
  for (let i = out.length; i < len; i++) out.push(rand(all));

  // Fisher–Yates shuffle so the required-class chars aren't always at the
  // start — keeps the result indistinguishable from pure random.
  for (let i = out.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0]! % (i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out.join("");
}

interface OcStep4PasswordProps {
  config: OcPortalConfig;
  state: OcFlowState;
  setState: (next: OcFlowState) => void;
}

function ocGetSlug(): string {
  const segments = window.location.pathname.split("/").filter(Boolean);
  return segments[0] ?? "";
}

export function OcStep4Password({ config, state, setState }: OcStep4PasswordProps) {
  const slug = useMemo(ocGetSlug, []);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [copied, setCopied] = useState(false);

  // Clear the "Copied!" tag a few seconds after it appears.
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  const ocHandleGenerate = async () => {
    const pwd = ocGeneratePassword();
    setNewPassword(pwd);
    setConfirmPassword(pwd);
    setShowNew(true);
    setShowConfirm(true);
    setErrorMessage(null);
    // Best-effort clipboard write. Permissions can deny it (iframe, http,
    // user denied) — in that case we just skip the badge without erroring.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pwd);
        setCopied(true);
      }
    } catch {
      /* clipboard unavailable — generated value is still in the field */
    }
  };

  const ocHandleCopy = async () => {
    if (!newPassword) return;
    try {
      await navigator.clipboard.writeText(newPassword);
      setCopied(true);
    } catch {
      /* ignore */
    }
  };

  const mismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit =
    !pending &&
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    newPassword === confirmPassword;

  const ocHandleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!state.sessionId) {
      setErrorMessage("Your session expired. Please start over.");
      setSessionExpired(true);
      return;
    }
    setPending(true);
    setErrorMessage(null);
    try {
      const res = await ocReset(slug, {
        sessionId: state.sessionId,
        newPassword,
      });
      if (res.ok) {
        setState({ ...state, step: "done" });
        return;
      }
      if (res.error === "password_policy") {
        setErrorMessage(
          res.message ??
            "That password does not meet your IT policy. Please try a different one."
        );
        return;
      }
      if (res.error === "not_verified") {
        setErrorMessage("Your session expired. Please start over.");
        setSessionExpired(true);
        return;
      }
      if (res.error === "dispatch_failed") {
        // The directory rejected the change (Azure / on-prem). Surface a
        // helpful message without leaking the underlying code to end users.
        setErrorMessage(
          "We could not apply your new password right now. Please try again, or contact your IT helpdesk."
        );
        return;
      }
      setErrorMessage("Something went wrong. Please try again.");
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setPending(false);
    }
  };

  const ocHandleStartOver = () => {
    setState({ step: "identifier" });
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl font-semibold text-slate-900">Choose a new password</h1>
      <p className="mt-2 text-sm text-slate-600">
        Pick a password you have not used before. Your IT policy may require uppercase, lowercase,
        digits, and a minimum length.
      </p>

      <form onSubmit={ocHandleSubmit} className="mt-6 space-y-4" noValidate>
        <div>
          <label
            htmlFor="oc-new-password"
            className="block text-sm font-medium text-slate-700"
          >
            New password
          </label>
          <div className="mt-1 relative">
            <input
              id="oc-new-password"
              type={showNew ? "text" : "password"}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={pending || sessionExpired}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 pr-20 text-sm text-slate-900 outline-none transition focus:border-[var(--oc-primary)] focus:ring-2 focus:ring-[var(--oc-primary)]/30 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-slate-500 hover:text-slate-700"
              tabIndex={-1}
            >
              {showNew ? "Hide" : "Show"}
            </button>
          </div>
          <div className="mt-2">
            <OcPasswordStrengthMeter password={newPassword} />
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs">
            <button
              type="button"
              onClick={ocHandleGenerate}
              disabled={pending || sessionExpired}
              className="inline-flex items-center gap-1.5 font-medium text-[var(--oc-primary)] hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5"
                aria-hidden="true"
              >
                <path d="M21 2v6h-6" />
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                <path d="M3 22v-6h6" />
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
              Generate strong password
            </button>
            {newPassword && (
              <button
                type="button"
                onClick={ocHandleCopy}
                disabled={pending || sessionExpired}
                className="inline-flex items-center gap-1.5 font-medium text-slate-600 hover:text-slate-900 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy
              </button>
            )}
            {copied && (
              <span role="status" className="font-medium text-green-700">Copied!</span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {config.passwordHint ?? "At least 12 characters, mixed case, and a digit."}
          </p>
        </div>

        <div>
          <label
            htmlFor="oc-confirm-password"
            className="block text-sm font-medium text-slate-700"
          >
            Confirm password
          </label>
          <div className="mt-1 relative">
            <input
              id="oc-confirm-password"
              type={showConfirm ? "text" : "password"}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={pending || sessionExpired}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 pr-20 text-sm text-slate-900 outline-none transition focus:border-[var(--oc-primary)] focus:ring-2 focus:ring-[var(--oc-primary)]/30 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-slate-500 hover:text-slate-700"
              tabIndex={-1}
            >
              {showConfirm ? "Hide" : "Show"}
            </button>
          </div>
          {mismatch && (
            <p className="mt-1 text-xs text-amber-700">Passwords do not match.</p>
          )}
        </div>

        {errorMessage && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        {sessionExpired ? (
          <button
            type="button"
            onClick={ocHandleStartOver}
            className="w-full rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Start over
          </button>
        ) : (
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50"
            style={{ backgroundColor: "var(--oc-primary)" }}
          >
            {pending ? "Resetting..." : "Reset password"}
          </button>
        )}
      </form>
    </div>
  );
}

export default OcStep4Password;
